import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge has to know this package's custom FONT-SIZE names.
 *
 * Without this it classifies any unrecognised `text-*` as a colour, so a size
 * and a colour in the same call look like the same conflict group and the
 * later one silently deletes the earlier:
 *
 *   cn("text-secondary", "text-muted")  ->  "text-muted"
 *
 * The size vanishes, the element falls back to the inherited font, and nothing
 * errors. This is not hypothetical — it shipped: every tab in the products
 * toolbar rendered at 13px/19.5px instead of 12px/normal, which made the
 * toolbar 6px too tall and pushed the whole table down the page.
 *
 * Registering the scale makes `text-secondary` a size and `text-muted` a
 * colour, so both survive.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ["title", "section", "body", "secondary", "label", "mono", "micro"],
      radius: ["kbd", "control", "panel", "card", "pill"],
      shadow: ["menu", "tooltip"],
    },
  },
});

/**
 * Merge Tailwind classes so the LAST conflicting utility wins.
 *
 * Plain string concatenation does not do this. `"px-4" + " " + "px-2"` emits
 * both classes and the winner is then decided by their order in the generated
 * stylesheet — not by the caller. The result is a component whose `className`
 * prop appears to work everywhere except the cases where it silently does
 * nothing, which is the hardest kind of styling bug to see in review.
 *
 * Every primitive in this package composes its classes through here.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
